import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  Legend,
} from "recharts";
import { fetchWasteByLoadingCode } from "../../store/actions/dashboard";
import { currentYear } from "../../utils/tools";
import { Spinner } from "react-bootstrap";

const SELECT_STYLE = {
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "6px 12px",
  color: "#0B1157",
  fontSize: 14,
  cursor: "pointer",
  outline: "none",
};

const COLOR = {
  recyklacia: "#0B1157",
  likvidacia: "#4DFFFF",
  ine: "#a0aec0",
};

const fmt = (num) => String(num).replace(".", ",");

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const { total, type } = payload[0].payload;
  const typeLabel = type === "recyklacia" ? "Recyklácia" : type === "likvidacia" ? "Likvidácia" : "Iné";
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 10,
      padding: "10px 16px",
      boxShadow: "0 4px 16px rgba(11,17,87,0.10)",
      color: "#0B1157",
      fontSize: 13,
    }}>
      <p style={{ margin: 0, fontWeight: 700, marginBottom: 4 }}>{label}</p>
      <p style={{ margin: 0, color: "#718094", marginBottom: 4 }}>{typeLabel}</p>
      <p style={{ margin: 0 }}>
        <strong>{fmt(total)} t</strong>
      </p>
    </div>
  );
};

const LegendItems = () => (
  <div className="d-flex gap-4 mt-2" style={{ fontSize: 13, color: "#718094" }}>
    {[
      { color: COLOR.recyklacia, label: "Recyklácia (R1–R13)" },
      { color: COLOR.likvidacia, label: "Likvidácia (D1–D15)" },
      { color: COLOR.ine, label: "Iné" },
    ].map(({ color, label }) => (
      <div key={label} className="d-flex align-items-center gap-2">
        <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }} />
        {label}
      </div>
    ))}
  </div>
);

const YEAR = currentYear();

const LoadingCodeChart = () => {
  const dispatch = useDispatch();
  const branches = useSelector((state) => state.branch.data);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(YEAR);
  const [branchId, setBranchId] = useState("");

  useEffect(() => {
    setLoading(true);
    dispatch(fetchWasteByLoadingCode({ year, branchId: branchId || null }))
      .unwrap()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch, year, branchId]);

  const years = Array.from({ length: 5 }, (_, i) => YEAR - i);
  const isEmpty = !loading && data.length === 0;

  return (
    <div style={{
      background: "#fff",
      borderRadius: 16,
      padding: "24px 24px 16px",
      boxShadow: "0 2px 12px rgba(11,17,87,0.07)",
      height: "100%",
    }}>
      <div className="mb-4">
        <h6 style={{ color: "#0B1157", fontWeight: 700, margin: 0, fontSize: 16 }}>
          Aktivita podľa kódu nakladania
        </h6>
        <p style={{ color: "#718094", fontSize: 13, margin: "2px 0 12px" }}>
          Recyklácia vs Likvidácia — objem v tonách
        </p>
        <div className="d-flex gap-2">
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)} style={SELECT_STYLE}>
            <option value="">Všetky prevádzkarne</option>
            {branches.map((b) => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={SELECT_STYLE}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: 280 }}>
          <Spinner animation="border" style={{ color: "#0B1157" }} />
        </div>
      ) : isEmpty ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: 280, color: "#718094", fontSize: 14 }}>
          Pre zvolené obdobie nie sú žiadne dáta.
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fill: "#718094", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                unit=" t"
                tickFormatter={fmt}
              />
              <YAxis
                type="category"
                dataKey="code"
                tick={{ fill: "#0B1157", fontSize: 12, fontWeight: 600 }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f4f5fe" }} />
              <Bar dataKey="total" radius={[0, 4, 4, 0]} maxBarSize={22}>
                {data.map((entry, i) => (
                  <Cell key={i} fill={COLOR[entry.type]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <LegendItems />
        </>
      )}
    </div>
  );
};

export default LoadingCodeChart;
